/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT Licence.
 * See LICENSE in the project root for licence information.
 */

/* global Office Word */

import { aiDraft, AiDraftRequest, AiDraftOption } from "../services/ai.service";
import { getDocumentInfo } from "../utils/document.utils";
import { convertMarkdownToHtml, isHtmlContent, stripMarkdown } from "../utils/markdown.utils";
import { getAccessToken } from "../utils/storage.utils";

let officeReady = false;
let wordReady = false;

Office.onReady((info) => {
  console.log("Office.js initialized:", info.host, info.platform);
  officeReady = true;

  if (info.host === Office.HostType.Word) {
    wordReady = true;
    console.log("Word API is available");
  }
});

function isOfficeReady(): boolean {
  if (!officeReady) {
    console.error("Office.js is not ready");
    return false;
  }
  return true;
}

function isWordReady(): boolean {
  if (!wordReady || typeof Word === "undefined") {
    console.error("Word API is not ready");
    return false;
  }
  return true;
}

// Helper to check if error is 401
function isUnauthorizedError(error: any): boolean {
  return error.response && error.response.status === 401;
}

// Helper to open the taskpane
async function openTaskpane() {
  if (Office.addin && Office.addin.showAsTaskpane) {
    return Office.addin.showAsTaskpane().catch((error) => {
      console.error("Failed to show taskpane:", error);
    });
  } else {
    console.warn("Office.addin.showAsTaskpane is not available.");
    return Promise.resolve();
  }
}

let dialog: Office.Dialog | null = null;

function showLoadingDialog(): Promise<boolean> {
  return new Promise((resolve) => {
    const loadingUrl = `${process.env.APP_URL}/loading.html`;
    
    Office.context.ui.displayDialogAsync(
      loadingUrl,
      { height: 50, width: 50, displayInIframe: true },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.warn("[AILA] Could not show loading dialog:", asyncResult.error.message);
          resolve(false);
        } else {
          dialog = asyncResult.value;
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, () => {});
          resolve(true);
        }
      }
    );
  });
}

function closeLoadingDialog(): void {
  if (dialog) {
    dialog.close();
    dialog = null;
  }
}

interface ToastMessage {
  type: "info" | "error" | "success" | "warning";
  message: string;
}

const TOAST_STORAGE_KEY = "aila_command_toast";

function sendMessageToTaskpane(message: ToastMessage): void {
  try {
    sessionStorage.setItem(TOAST_STORAGE_KEY, JSON.stringify({
      ...message,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.warn("[AILA] Could not write to sessionStorage:", e);
  }
}

/**
 * Common handler for AI actions
 */
async function handleAiAction(option: AiDraftOption, event: Office.AddinCommands.Event) {
  console.log(`[AILA] ${option} action triggered at ${new Date().toISOString()}`);

  let didComplete = false;
  const completeEvent = () => {
    if (!didComplete) {
      console.log(`[AILA] ${option} event.completed() called`);
      event.completed();
      didComplete = true;
    } else {
      console.warn(`[AILA] ${option} event.completed() already called, skipping`);
    }
  };

  // Phase 1: Check Office/Word readiness (await onReady to handle cold start)
  const officeInfo = await Office.onReady();
  if (!officeInfo.host) {
    console.error("[AILA] Office.js not ready");
    completeEvent();
    return;
  }

  if (officeInfo.host !== Office.HostType.Word || typeof Word === "undefined") {
    console.error("[AILA] Word API not ready");
    completeEvent();
    return;
  }

  // Phase 2: Check authentication
  const token = await getAccessToken();
  if (!token) {
    console.log("[AILA] No access token found. Opening taskpane for login.");
    await openTaskpane();
    sendMessageToTaskpane({ type: "error", message: "Please log in to use this feature." });
    completeEvent();
    return;
  }

  // Get selected text first to validate selection
  let selectedText = "";
  try {
    selectedText = await new Promise<string>((resolve, reject) => {
      Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(String(result.value || ""));
        } else {
          reject(new Error(result.error?.message || "Failed to get selected text"));
        }
      });
    });
  } catch (selectionError) {
    console.error("[AILA] Error getting selected text:", selectionError);
    completeEvent();
    return;
  }

  console.log(`[AILA] Selected text length: ${selectedText?.length || 0}`);

  // Phase 3: Check if text is selected
  if (!selectedText || selectedText.trim().length === 0) {
    console.log("[AILA] No text selected");
    sendMessageToTaskpane({ type: "info", message: "Please select some text first, then right-click to use AILA features." });
    completeEvent();
    return;
  }

  // Show loading dialog
  await showLoadingDialog();

  try {
    // Get document info
    const documentInfo = await getDocumentInfo();
    const documentId = documentInfo.id;
    console.log(`[AILA] Document ID: ${documentId}`);

    const request: AiDraftRequest = {
      document_id: documentId || "",
      text_selected: selectedText,
    };

    let response;

    if (option === AiDraftOption.DraftFullDocument) {
      console.log("[AILA] Processing DraftFullDocument...");
      response = await processDraftFullDocument(request);
    } else {
      console.log(`[AILA] Calling API for ${option}...`);
      response = await aiDraft(option, request);
    }

    console.log(`[AILA] API response received, inserting result...`);

    // Insert the result
    const htmlResponse = isHtmlContent(response.response)
      ? response.response
      : convertMarkdownToHtml(response.response);

    await insertContentIntoDocument(htmlResponse);

    console.log(`[AILA] ${option} completed successfully`);
    closeLoadingDialog();
    completeEvent();
  } catch (apiError: any) {
    console.error("[AILA] API Error:", apiError);
    closeLoadingDialog();

    if (isUnauthorizedError(apiError)) {
      console.log("[AILA] Unauthorized (401). Opening taskpane.");
      await openTaskpane();
      sendMessageToTaskpane({ type: "error", message: "Your session has expired. Please log in again." });
    } else {
      const errorMessage = apiError?.message || "An error occurred";
      sendMessageToTaskpane({ type: "error", message: errorMessage });
    }
    completeEvent();
  }
}

async function processDraftFullDocument(request: AiDraftRequest) {
  const outlineResponse = await aiDraft(AiDraftOption.DraftOutline, request);
  const outlineHtml = convertMarkdownToHtml(outlineResponse.response);

  const baseDocumentRequest: AiDraftRequest = {
    document_id: request.document_id,
    text_selected: request.text_selected,
    outline: outlineHtml,
    document_length: "short",
  };

  const sections: string[] = [];
  const maxSections = 20;

  const fetchSection = async (
    nextSection: boolean,
    threadId?: string,
    remainingSections: number = maxSections
  ): Promise<{ response: string; thread_id?: string; stopped: boolean }> => {
    if (remainingSections <= 0) {
      console.warn("[AILA] DraftFullDocument reached max sections");
      sendMessageToTaskpane({ type: "warning", message: "Draft truncated after 20 sections." });
      return {
        response: sections.join("\n"),
        thread_id: threadId,
        stopped: true,
      };
    }

    const documentRequest: AiDraftRequest = {
      ...baseDocumentRequest,
      next_section: nextSection,
    };

    if (threadId) {
      documentRequest.thread_id = threadId;
    }

    let documentResponse;
    try {
      documentResponse = await aiDraft(AiDraftOption.DraftFullDocument, documentRequest);
    } catch (error) {
      console.error("[AILA] fetchSection aiDraft error, returning partial content:", error);
      return {
        response: sections.join("\n"),
        thread_id: threadId,
        stopped: true,
      };
    }

    sections.push(documentResponse.response);

    const nextThreadId = documentResponse.thread_id || threadId;
    const stopped = documentResponse.stopped;
    const isStopped = stopped === true || stopped === "true";

    if (isStopped) {
      return {
        response: sections.join("\n"),
        thread_id: nextThreadId,
        stopped: true,
      };
    }

    return fetchSection(true, nextThreadId, remainingSections - 1);
  };

  return fetchSection(false, outlineResponse.thread_id);
}

async function insertContentIntoDocument(htmlContent: string): Promise<void> {
  if (!isWordReady()) {
    throw new Error("Word API not ready");
  }

  try {
    await Word.run(async (context) => {
      const range = context.document.getSelection();
      range.insertHtml(htmlContent, Word.InsertLocation.replace);
      await context.sync();
    });
  } catch (wordError) {
    console.error("[AILA] Failed to insert HTML, falling back to plain text:", wordError);
    try {
      const plainText = stripMarkdown(htmlContent);
      await Word.run(async (context) => {
        const range = context.document.getSelection();
        range.insertText(plainText, Word.InsertLocation.replace);
        await context.sync();
      });
    } catch (textError) {
      console.error("[AILA] Failed to insert plain text:", textError);
      throw new Error("Failed to insert content into document");
    }
  }
}

async function writeClause(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.WriteClause, event);
}

async function writeDefinition(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.WriteDefinition, event);
}

async function makeShorter(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.MakeShorter, event);
}

async function makeLonger(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.MakeLonger, event);
}

async function simplifyLanguage(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.SimplifyLanguage, event);
}

async function complexifyLanguage(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.ComplexifyLanguage, event);
}

async function summarise(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.Summarise, event); // Note: API endpoint uses 'summarizer' based on Vue component
}

async function draftFullDocument(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.DraftFullDocument, event);
}

async function draftOutline(event: Office.AddinCommands.Event) {
  await handleAiAction(AiDraftOption.DraftOutline, event);
}

// Register the functions with Office.

Office.actions.associate("writeClause", writeClause);
Office.actions.associate("writeDefinition", writeDefinition);
Office.actions.associate("makeShorter", makeShorter);
Office.actions.associate("makeLonger", makeLonger);
Office.actions.associate("simplifyLanguage", simplifyLanguage);
Office.actions.associate("complexifyLanguage", complexifyLanguage);
Office.actions.associate("summarise", summarise);
Office.actions.associate("draftFullDocument", draftFullDocument);
Office.actions.associate("draftOutline", draftOutline);
